import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  Input,
  HostListener,
  inject,
} from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStateService } from 'app/core/services/auth-state.service';
import { AppConstants } from 'app/app.constants';
import { AuthorizationService } from 'app/core/services/authorization.service';
import { ChangeLanguageService } from 'app/core/services/changeLanguage.service';
import { FleetMonitoringService } from 'app/core/services/fleet-monitoring.service';
import { LoginService } from 'app/core/services/login.service';
import { NvgTrabalhoService } from 'app/core/services/nvg-trabalho.service';
import { UserService } from 'app/core/services/user.service';
import { User } from 'app/shared/models/user';
import { _roleEnumUserClass } from 'app/utils/role.utils';
import { getUserInitials } from 'app/utils/user-initials.utils';
import { CurrentViewportBreakpoint } from '../layouts/basicLayout.component';
import { WhatsappIcon } from 'app/utils/whatsapp-icon-utils';

/** One entry of the header language menu. */
export interface HeaderLanguageOption {
  /** App language code, as stored in local storage. */
  value: string;
  /** Short code shown on the language button and in the menu. */
  code: string;
  /** Translation key of the language name, shown in the current UI language. */
  nameKey: string;
}

/** Same order as the tecomec.com.br language menu. */
const LANGUAGE_OPTIONS: readonly HeaderLanguageOption[] = [
  { value: AppConstants.LANGUAGES.PT_BR, code: 'PT', nameKey: 'global.ptBR' },
  { value: AppConstants.LANGUAGES.ES, code: 'ES', nameKey: 'global.es' },
  { value: AppConstants.LANGUAGES.EN, code: 'EN', nameKey: 'global.en' },
  { value: AppConstants.LANGUAGES.IT, code: 'IT', nameKey: 'global.it' },
];

/** Menu entry of a language; unknown languages resolve to ptBR. */
function findLanguageOption(language: string): HeaderLanguageOption {
  return (
    LANGUAGE_OPTIONS.find((option) => option.value === language) ??
    LANGUAGE_OPTIONS[0]
  );
}

/** CSS class of the list item that holds the language button and menu. */
const LANGUAGE_MENU_CONTAINER = '.header__nav--language';

@Component({
  standalone: true,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [RouterLink, TranslateModule],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly authState = inject(AuthStateService);
  private readonly destroy$ = new Subject<void>();

  elem = document.documentElement;
  /**
   * Header of the pages before the login (login, sign-up, password recovery):
   * the brand, WhatsApp and the language button, without the menu button and
   * the profile menu.
   */
  @Input() publicPage = false;
  @Input() hideMenuButton = false;
  @Input() resizeEventEmitter: EventEmitter<CurrentViewportBreakpoint>;
  @Input() toggleSidebarSubject: Subject<boolean | void>;
  @Output() clickOutside = new EventEmitter<void>();
  nomeUsuarioLogado = '';
  userRole = '';
  ROLE_USERS_ENUM = _roleEnumUserClass.ROLE_ENUM;
  wasInsideOfProfileMenu = false;

  @HostListener('document:click', ['$event'])
  clickout(event) {
    // The click may land on the avatar's fallback icon, inside the button.
    const profileButton = document.getElementById('profileMenu');
    const clickedProfileButton = Boolean(profileButton?.contains(event.target));
    if (this.wasInsideOfProfileMenu && !clickedProfileButton) {
      this.wasInsideOfProfileMenu = false;
      this.toggleMenu();
    }
    const clickedLanguageMenu = Boolean(
      event.target?.closest?.(LANGUAGE_MENU_CONTAINER),
    );
    if (this.isLanguageMenuOpen && !clickedLanguageMenu) {
      this.isLanguageMenuOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  closeLanguageMenu(): void {
    this.isLanguageMenuOpen = false;
  }

  public auth;
  public currentLanguage: string = AppConstants.LANGUAGES.PT_BR;
  public isLanguageMenuOpen = false;
  public showAll = false;
  public nome;
  public tokenInterval;
  public usuario: User;
  public deviceList: Array<any>;
  public isFreeTier = false;

  public linkWatsapp: string;

  constructor(
    public loginService: LoginService,
    public router: Router,
    public translate: TranslateService,
    public monitoramentoFrotaService: FleetMonitoringService,
    public nvgTrabalhoService: NvgTrabalhoService,
    public changeLanguageService: ChangeLanguageService,
    public cdRef: ChangeDetectorRef,
    public userService: UserService,
  ) {
    this.translate.get('sidebar.title-customers').subscribe((res: string) => {
      this.nome = res;
    });
    const whatsapp = new WhatsappIcon('554131323300', 'Olá, vim pelo isoFarm!');
    this.linkWatsapp = whatsapp.whatsappLink;
  }

  /** Initials shown in the avatar; empty until the user's name is known. */
  get userInitials(): string {
    return getUserInitials(this.nomeUsuarioLogado);
  }

  /** Entries of the language menu. */
  get languageOptions(): readonly HeaderLanguageOption[] {
    return LANGUAGE_OPTIONS;
  }

  /** Short code of the current language, shown on the language button. */
  get languageCode(): string {
    return findLanguageOption(this.currentLanguage).code;
  }

  ngOnInit() {
    this.currentLanguage =
      localStorage.getItem(AppConstants.KEYS_LOCAL_STORAGE.ISO_LANG) ||
      AppConstants.LANGUAGES.PT_BR;

    // The public header has no profile menu, so it does not need the user.
    // Right after the login the session user has no name yet, and fetching it
    // here too would repeat the request the app header makes next.
    if (!this.publicPage) {
      this.watchLoggedUser();
    }
    if (!!this.resizeEventEmitter) {
      this.resizeEventEmitter.subscribe(
        (currentViewportBreakpoint: CurrentViewportBreakpoint) => {
          if (
            currentViewportBreakpoint.tablet ||
            currentViewportBreakpoint.mobile
          ) {
            this.hideMenuButton = false;
          }
          if (currentViewportBreakpoint.desktop) {
            this.hideMenuButton = true;
          }
        },
      );
    }
  }

  /** Keeps the role and the name of the profile menu in sync with the session. */
  private watchLoggedUser(): void {
    this.authState.user$
      .pipe(
        filter((user) => !!user),
        takeUntil(this.destroy$),
      )
      .subscribe((loggedUser: any) => {
        this.userRole = this.authorizationService.getLoggedUserRole();
        if (!loggedUser.name) {
          this.userService
            .getUserByEmail(loggedUser.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((user) => {
              const loggedUserWithName = { ...loggedUser, name: user.name };
              this.authState.setSession(
                this.authState.getToken() ?? '',
                loggedUserWithName,
              );
              this.nomeUsuarioLogado = user.name;
            });
        } else {
          this.nomeUsuarioLogado = loggedUser.name;
        }
      });
  }

  openUserDropdown() {
    this.toggleMenu();
    this.wasInsideOfProfileMenu = !this.wasInsideOfProfileMenu;
  }

  toggleMenu(): void {
    const subMenu = document.getElementById('subMenu');
    subMenu.classList.toggle('header__nav--profile---wrap----open');
  }
  toggleSidebar(): void {
    if (this.toggleSidebarSubject) this.toggleSidebarSubject.next();
  }

  toggleLanguageMenu(): void {
    this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
  }

  changeLanguage(language) {
    if (language == null || language == undefined) {
      language = AppConstants.LANGUAGES.PT_BR;
    }

    this.isLanguageMenuOpen = false;
    this.translate.use(language);
    localStorage.setItem(AppConstants.KEYS_LOCAL_STORAGE.ISO_LANG, language);
    this.changeLanguageService.changeData(language);
    this.currentLanguage = language;
    // Before the login there is no account to save the choice to; the sign-up
    // and the next login read it from local storage.
    if (this.publicPage) {
      return;
    }
    // The explicit choice becomes the account's preferred language. It is a
    // background save: a failure is ignored and retried on the next change.
    this.userService
      .updatePreferredLanguage(language)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => undefined,
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout() {
    window.sessionStorage.clear();
    this.authState.clearUserScopedData();
    this.authState.clearSession();
    window.localStorage.removeItem(AppConstants.KEYS_LOCAL_STORAGE.HIDE_MODAL);
    clearInterval(this.tokenInterval);
    this.router.navigate(['/login']);
  }
}
